import React, { useEffect, useMemo, useState } from "react";
import API from "../api";
import Navbar from "../components/Navbar";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";

const STATUS_MAP = {
  1: "proposed",
  2: "confirmed",
  3: "delivered",
  4: "cancelled",
  5: "missed",
  6: "completed"
};

const getStatusLabel = (status) => {
  return STATUS_MAP[parseInt(status)] || status;
};

const AppointmentDashboard = () => {
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    status: "",
    visit: ""
  });

  // 🔥 UPDATED STATS
  const [stats, setStats] = useState({
    today: 0,
    week: 0,
    month: 0,
    total: 0,
    todayVisited: 0,
    totalBooked: 0
  });

  const [analytics, setAnalytics] = useState({
    visited: 0,
    missed: 0,
    completionRate: 0
  });

  // 🔥 MAIN LOGIC (IMPORTANT)
  const calculateStats = (data) => {
    const todayStr = new Date().toISOString().split("T")[0];

    let today = 0,
      week = 0,
      month = 0,
      total = 0,
      todayVisited = 0;

    let visited = 0,
      missed = 0;

    const now = new Date();

    Object.keys(data || {}).forEach((date) => {
      const appts = data[date]?.appointments || [];
      const apptDate = new Date(date);

      const diffDays = Math.ceil(
        (apptDate - now) / (1000 * 60 * 60 * 24)
      );

      total += appts.length;

      appts.forEach((a) => {
        if (a?.visit_status === "came") visited++;
        else missed++;
      });

      // 🔥 TODAY LOGIC
      if (date === todayStr) {
        today += appts.length;

        appts.forEach((a) => {
          if (a?.visit_status === "came") todayVisited++;
        });
      }

      // 🔥 FUTURE
      if (diffDays >= 0 && diffDays <= 7) week += appts.length;
      if (diffDays >= 0 && diffDays <= 30) month += appts.length;
    });

    setStats({
      today,
      week,
      month,
      total,
      todayVisited,
      totalBooked: total
    });

    setAnalytics({
      visited,
      missed,
      completionRate: total
        ? ((visited / total) * 100).toFixed(1)
        : 0
    });
  };

  const fetchAppointments = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const res = await API.post(
        "/all_appointment",
        {},
        {
          params: filters,
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = res?.data || {};
      setGrouped(data);
      calculateStats(data);
    } catch (err) {
      console.error(err);
      setGrouped({});
      calculateStats({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, [filters]);

  const chartData = useMemo(
    () => [
      { name: "Visited", value: analytics.visited },
      { name: "Missed", value: analytics.missed }
    ],
    [analytics]
  );

  return (
    <div className="flex flex-col h-screen">
      <Navbar />

      <div style={styles.container}>
       <div style={styles.header}>
  <div>
    <h1 style={styles.title}>🏥 Patient Dashboard</h1>
    <p style={styles.subtitle}>
      Monitor appointments, visits, and patient activity
    </p>
  </div>

  <div style={styles.headerRight}>
    <span style={styles.date}>
      {new Date().toDateString()}
    </span>
  </div>
</div>

        {/* 🔥 STATS */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <h4>📅 Today</h4>
            <p>{stats.today}</p>
          </div>

          <div style={styles.statCardGreen}>
            <h4>🟢 Visited Today</h4>
            <p>{stats.todayVisited}</p>
          </div>

          <div style={styles.statCard}>
            <h4>📆 Next 7 Days</h4>
            <p>{stats.week}</p>
          </div>

          <div style={styles.statCard}>
            <h4>🗓 Next 30 Days</h4>
            <p>{stats.month}</p>
          </div>

          <div style={styles.statCardBlue}>
            <h4>📊 Total Booked</h4>
            <p>{stats.totalBooked}</p>
          </div>
        </div>

        {/* ANALYTICS */}
        <div style={styles.statsGrid}>
          <div style={styles.statCardGreen}>
            <h4>Visited</h4>
            <p>{analytics.visited}</p>
          </div>

          <div style={styles.statCardRed}>
            <h4>Missed</h4>
            <p>{analytics.missed}</p>
          </div>

          <div style={styles.statCardBlue}>
            <h4>Completion</h4>
            <p>{analytics.completionRate}%</p>
          </div>
        </div>

        {/* ALERT */}
        <div style={styles.alertBox}>
          🚨 Missed Patients: {analytics.missed}
        </div>

        {/* CHART */}
        <div style={styles.chartBox}>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* FILTERS */}
        <div style={styles.filterBox}>
          <select onChange={(e)=>setFilters({...filters,status:e.target.value})}>
            <option value="">All Status</option>
            <option value="2">Confirmed</option>
            <option value="5">Missed</option>
          </select>

          <select onChange={(e)=>setFilters({...filters,visit:e.target.value})}>
            <option value="">All Visit</option>
            <option value="came">Visited</option>
            <option value="not_came">Not Visited</option>
          </select>
        </div>

        {/* CALENDAR */}
        {Object.keys(grouped || {}).map((date)=>(
          <div key={date} style={styles.dayColumn}>
            <h3>{date} ({grouped[date]?.day_type})</h3>

            {(grouped[date]?.appointments || []).map((appt,i)=>(
              <div key={i} style={styles.eventBox}>
                <b>{appt.time}</b> - {appt.patient}
                <br/>
                Status: {getStatusLabel(appt.appointment_status)}
                <br/>
                Visit: {appt.visit_status === "came" ? "✅ Visited" : "❌ Not Visited"}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AppointmentDashboard;

// 🎨 STYLES
const styles = {
  container:{padding:20,background:"#f5f7fb",flex:1,  marginTop: "70px"},
  title:{fontSize:26,fontWeight:700},
  statsGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10},
  statCard:{background:"#fff",padding:10,borderRadius:10,textAlign:"center"},
  statCardGreen:{background:"#dcfce7",padding:10,borderRadius:10,textAlign:"center"},
  statCardRed:{background:"#fee2e2",padding:10,borderRadius:10,textAlign:"center"},
  statCardBlue:{background:"#dbeafe",padding:10,borderRadius:10,textAlign:"center"},
  alertBox:{background:"#fef3c7",padding:10,margin:"10px 0"},
  chartBox:{background:"#fff",padding:10,marginBottom:10},
  filterBox:{display:"flex",gap:10,marginBottom:10},
  dayColumn:{background:"#fff",padding:10,marginBottom:10},
  eventBox:{background:"#f9fafb",padding:8,marginTop:5}
};